import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  TextField,
  Box,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { loadStripe } from '@stripe/stripe-js';
import { requestCode, signUp, requestEmailCode, signUpEmail } from '../api';
import { ctaColors } from '../utils/color';

// ACOS: resolve the per-fleet auth config (fleets.reservation_auth_methods) into the ordered
// list of methods this dialog offers. null / unknown ⇒ phone-only (backwards compatible).
//   'phone' ⇒ ['phone']   'email' ⇒ ['email']   'both' ⇒ ['phone', 'email'] (an OR — pick one)
const resolveMethods = (authMethods) => {
  if (authMethods === 'email') return ['email'];
  if (authMethods === 'both') return ['phone', 'email'];
  return ['phone'];
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginDialog = ({
  open,
  onClose,
  onSuccess,
  title = 'Verify to finish your booking',
  successCallback = null, // Optional callback for custom success handling
  initialPhoneNumber = '', // Optional initial phone number
  requirePayment = true, // Unmonetized fleets skip the Stripe requirement (no stripe_pk needed)
  authMethods = 'phone', // ACOS: per-fleet auth config ('phone' | 'email' | 'both'; null ⇒ phone)
  fleetId = null, // ACOS: threaded into signUp / signUpEmail so the backend approval gate can apply
  primaryColor = '#004990' // ACOS: fleet brand primary (navy for BallyQ); drives CTA + accents
}) => {
  const methods = resolveMethods(authMethods);
  // A white brand color would draw white-on-white CTAs; ctaColors swaps in navy and picks readable text.
  const { background: brandColor, text: brandText } = ctaColors(primaryColor);
  const showChooser = methods.length > 1;

  const [method, setMethod] = useState(methods[0]); // active verification method: 'phone' | 'email'
  const [step, setStep] = useState('entry'); // 'entry' (phone/email input) or 'code'
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Keep the active method in sync when the fleet config (authMethods) changes.
  useEffect(() => {
    setMethod(resolveMethods(authMethods)[0]);
  }, [authMethods]);

  // Update phone number when initialPhoneNumber prop changes
  useEffect(() => {
    let processedPhoneNumber = initialPhoneNumber;
    // If phone number is exactly 10 digits (US format without country code), add US country code
    if (/^\d{10}$/.test(initialPhoneNumber)) {
      processedPhoneNumber = `1${initialPhoneNumber}`;
    }
    setPhoneNumber(processedPhoneNumber);
  }, [initialPhoneNumber]);

  // Contrast-safe hover: darken the brand color slightly without pulling in a color lib.
  const ctaSx = {
    mt: 1,
    width: '100%',
    backgroundColor: brandColor,
    color: brandText,
    borderRadius: '10px',
    py: 1.4,
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': { backgroundColor: brandColor, filter: 'brightness(0.92)', boxShadow: 'none' },
    '&:disabled': { backgroundColor: '#cccccc', color: '#666' }
  };

  const handlePhoneEntry = async () => {
    setError('');
    setLoading(true);
    try {
      let processedPhoneNumber = phoneNumber;
      // If phone number is exactly 10 digits (US format without country code), add US country code
      if (/^\d{10}$/.test(phoneNumber)) {
        processedPhoneNumber = `1${phoneNumber}`;
        setPhoneNumber(processedPhoneNumber); // Update the display
      }

      const fullPhoneNumber = `+${processedPhoneNumber}`;
      const phoneNumberObject = parsePhoneNumberFromString(fullPhoneNumber);
      if (!phoneNumberObject) throw new Error('Invalid phone number');

      const localPhoneNumber = phoneNumberObject.nationalNumber;
      const countryCode = phoneNumberObject.countryCallingCode;

      localStorage.setItem('phone', localPhoneNumber);
      localStorage.setItem('country_code', countryCode);

      await requestCode(localPhoneNumber, countryCode);
      setStep('code');
    } catch (error) {
      setError('Failed to send SMS code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailEntry = async () => {
    setError('');
    if (!EMAIL_REGEX.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const operator = JSON.parse(localStorage.getItem('operator'));
      if (!operator || !operator.id) {
        setError('Operator information is missing. Please refresh the page.');
        setLoading(false);
        return;
      }
      await requestEmailCode(email.trim(), operator.id, fleetId);
      setStep('code');
    } catch (error) {
      setError('Failed to send email code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEntrySubmit = () => (method === 'email' ? handleEmailEntry() : handlePhoneEntry());

  const handleCodeSubmit = async () => {
    // Phone codes are 4 digits; email (Twilio Verify) codes may be longer.
    const codePattern = method === 'email' ? /^\d{4,8}$/ : /^\d{4}$/;
    if (!code.match(codePattern)) {
      setError('Please enter a valid code.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const operatorString = localStorage.getItem('operator');
      const operator = JSON.parse(operatorString);
      // Operator identity is always needed (for signup). Stripe is only required on the paid
      // path — unmonetized fleets have no stripe_pk and must not be blocked here.
      if (!operator || !operator.id) {
        console.error('Operator information is not defined.');
        setError('Operator information is missing. Please refresh the page.');
        setLoading(false);
        return;
      }
      if (requirePayment && !operator.stripe_pk) {
        console.error('Operator stripe_pk is not defined for a monetized fleet.');
        setError('Payment is not configured for this operator. Please contact support.');
        setLoading(false);
        return;
      }

      let user;
      if (method === 'email') {
        user = await signUpEmail(email.trim(), code, operator.id, fleetId);
      } else {
        const phone = localStorage.getItem('phone');
        const country_code = localStorage.getItem('country_code');
        user = await signUp(country_code, phone, code, operator.id, fleetId);
      }

      const waiver_status = user.waiver_status;
      const user_id = user.id;
      const stripe_customer_id = user.stripe_customer_id;
      localStorage.setItem('user', JSON.stringify(user));

      // Record how this booking was verified so the downstream waiver can lock the email when it
      // was verified by email (editing it there would silently overwrite the account's email).
      if (method === 'email') {
        localStorage.setItem('reservation_auth_method', 'email');
        localStorage.setItem('verified_email', email.trim());
      } else {
        localStorage.setItem('reservation_auth_method', 'phone');
        localStorage.removeItem('verified_email');
      }

      // Call success callback if provided, otherwise call default onSuccess
      try {
        if (successCallback) {
          await successCallback({
            user,
            user_id,
            waiver_status,
            stripe_customer_id,
            operator,
            loadStripe
          });
        } else {
          onSuccess(user);
        }

        // Close the dialog after successful authentication
        onClose();
        resetState();
      } catch (callbackError) {
        console.error('Error in success callback:', callbackError);
        setError('Authentication successful, but there was an issue proceeding. Please try again.');
      }
    } catch (error) {
      setError(`Failed to sign in. ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setStep('entry');
    setMethod(resolveMethods(authMethods)[0]);
    setPhoneNumber(initialPhoneNumber);
    setEmail('');
    setCode('');
    setError('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleMethodChange = (_e, next) => {
    if (!next || next === method) return;
    setMethod(next);
    setStep('entry');
    setCode('');
    setError('');
  };

  const entrySubtitle = method === 'email'
    ? "Enter your email and we'll send you a verification code."
    : "Enter your mobile number and we'll text you a verification code.";

  const codeSubtitle = method === 'email'
    ? 'Enter the verification code we emailed you.'
    : 'Enter the 4-digit code we texted to your phone.';

  const renderChooser = () => (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ mb: 1, color: '#555', textAlign: 'center' }}>
        Verify with your phone <strong>OR</strong> email — you only need one.
      </Typography>
      <ToggleButtonGroup
        value={method}
        exclusive
        onChange={handleMethodChange}
        fullWidth
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 600,
            borderColor: brandColor,
            color: brandText === '#fff' ? brandColor : '#1a1a1a',
            py: 1
          },
          '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: brandColor,
            color: brandText,
            '&:hover': { backgroundColor: brandColor, filter: 'brightness(0.92)' }
          }
        }}
      >
        <ToggleButton value="phone">Phone</ToggleButton>
        <ToggleButton value="email">Email</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  const renderEntryField = () => (
    method === 'email' ? (
      <TextField
        type="email"
        label="Email address"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !loading) handleEntrySubmit(); }}
        error={!!error}
        fullWidth
        autoFocus
      />
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <PhoneInput
          country={'us'}
          value={phoneNumber}
          onChange={setPhoneNumber}
          inputStyle={{ width: '100%' }}
          containerStyle={{ width: '100%' }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !loading) handleEntrySubmit(); }}
        />
      </Box>
    )
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, py: 3.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
          {step === 'entry' ? title : 'Enter your code'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2.5 }}>
          {step === 'entry' ? entrySubtitle : codeSubtitle}
        </Typography>

        {step === 'entry' ? (
          <>
            {showChooser && renderChooser()}
            {renderEntryField()}
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
                {error}
              </Typography>
            )}
            <Button onClick={handleEntrySubmit} variant="contained" disabled={loading} sx={ctaSx}>
              {loading ? 'Sending…' : 'Send code'}
            </Button>
          </>
        ) : (
          <>
            <TextField
              label="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter' && !loading) handleCodeSubmit(); }}
              error={!!error}
              helperText={error || ' '}
              fullWidth
              autoFocus
              inputProps={{ inputMode: 'numeric', maxLength: 8 }}
            />
            <Button onClick={handleCodeSubmit} variant="contained" disabled={loading} sx={ctaSx}>
              {loading ? 'Verifying…' : 'Verify'}
            </Button>
            <Button
              onClick={() => { setStep('entry'); setCode(''); setError(''); }}
              disabled={loading}
              sx={{ mt: 1, width: '100%', textTransform: 'none', color: '#666' }}
            >
              Back
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LoginDialog;
